import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material'
import { Search, FolderOpen, Description } from '@mui/icons-material'

const PublicHome = () => {
  const navigate = useNavigate()
  const [gestiones, setGestiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    loadGestiones()
  }, [])

  const loadGestiones = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/publico/gestiones')
      setGestiones(response.data || [])
    } catch (err) {
      console.error('Error cargando gestiones públicas:', err)
      setError('No se pudo cargar la información. Intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    const q = search.trim()
    navigate(q ? `/publico/procedimientos?search=${encodeURIComponent(q)}` : '/publico/procedimientos')
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Procedimientos Institucionales
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Consulte los procedimientos del Sistema de Gestión de Calidad de la ENSAS,
          organizados por gestión institucional.
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSearch} sx={{ maxWidth: 560, mx: 'auto', mb: 4 }}>
        <TextField
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por título, código o proceso..."
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Description />}
          onClick={() => navigate('/publico/procedimientos')}
        >
          Ver todos los procedimientos
        </Button>
      </Box>

      <Grid container spacing={2}>
        {gestiones.map((gestion) => (
          <Grid item xs={12} sm={6} md={4} key={gestion.id}>
            <Card
              sx={{
                height: '100%',
                borderTop: `6px solid ${gestion.color || '#64B5F6'}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
              }}
            >
              <CardActionArea
                onClick={() => navigate(`/publico/gestion/${gestion.id}`)}
                sx={{ height: '100%' }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FolderOpen sx={{ color: gestion.color || 'primary.main' }} />
                    <Chip
                      label={gestion.codigo}
                      size="small"
                      sx={{ bgcolor: gestion.color || '#64B5F6', color: '#fff', fontWeight: 700 }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {gestion.nombre}
                  </Typography>
                  {gestion.descripcion && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {gestion.descripcion}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {gestion.total_publicos} procedimiento{gestion.total_publicos !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {gestiones.length === 0 && (
        <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
          No hay gestiones disponibles por el momento.
        </Typography>
      )}
    </Container>
  )
}

export default PublicHome
